import type { TargetKind } from "./types";

/**
 * Display name per target kind. Exported (IR-1386 r3) because the type pie
 * legend must name its slices exactly as the table's badges do — two label
 * lists would let the chart and the column it summarises drift apart.
 *
 * 'unknown' carries a real word here on purpose: the badge renders it as an
 * em-dash (a blank-ish cell is fine in a row you can read the rest of), but a
 * pie slice labelled "—" is unreadable.
 */
export const TARGET_KIND_LABELS: Record<TargetKind, string> = {
  user: "User",
  bot: "Bot",
  public_group: "Public group",
  private_group: "Private group",
  public_channel: "Public channel",
  private_channel: "Private channel",
  unknown: "Unknown",
};

export function TargetKindBadge({ kind }: { kind: TargetKind }) {
  // Em-dash for 'unknown' and for any value not in LABELS (e.g. a transient
  // pre-visibility-backfill 'group'/'channel') so the cell never renders blank.
  const label = kind === "unknown" ? undefined : TARGET_KIND_LABELS[kind];
  if (!label) return <span className="text-[var(--iris-fg-4)]">—</span>;
  return (
    // IR-1386 r4: `whitespace-nowrap` — "Private channel" wrapping to two lines
    // made the badge, not the text, the thing that set the row height for the
    // whole table ("we dont need all this space", Rotem).
    <span className="inline-flex items-center whitespace-nowrap rounded-full border border-[var(--iris-line-2)] px-2 py-0.5 text-xs text-[var(--iris-fg-1)]">
      {label}
    </span>
  );
}
